import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Clock,
  DollarSign,
  User,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Percent,
  BadgeCheck,
  Briefcase,
  Receipt,
  CalendarClock,
  CalendarRange,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoField } from '@/components/common/InfoField';
import { QuickLinkButton } from '@/components/common/QuickLinkButton';
import { formatRelativeTime } from '@/lib/utils/date';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import type { Contract, Currency } from '../types';

export const formatContractCurrency = (amount: number, currency: Currency): string => {
  if (currency === 'UZS') {
    return new Intl.NumberFormat('uz-UZ', {
      style: 'currency',
      currency: 'UZS',
      maximumFractionDigits: 0,
    }).format(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

// For HOURLY contracts the API stores price=null; the actual contract value
// comes from the hourly package price converted to the contract currency.
// For SUCCESS_FEE, the relevant amount is claimedPrice.
export const getContractEffectivePrice = (contract: Contract): number => {
  if (contract.type === 'HOURLY' && contract.hourlyPackage?.price !== undefined) {
    const pkgPrice = contract.hourlyPackage.price;
    if (contract.currency === 'UZS' && contract.dolarRate) {
      return pkgPrice * contract.dolarRate;
    }
    return pkgPrice;
  }
  if (contract.type === 'SUCCESS_FEE') {
    return contract.claimedPrice ?? 0;
  }
  return contract.price ?? 0;
};

interface ContractCardProps {
  contract: Contract;
}

export function ContractInfoCard({ contract }: ContractCardProps) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <FileText className="h-5 w-5 text-slate-400" />
          {t('contractInformation')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <InfoField
          icon={FileText}
          label={t('name')}
          value={contract.name}
        />
        <InfoField
          icon={FileText}
          label={t('description')}
          value={contract.description}
        />
        <InfoField
          icon={BadgeCheck}
          label={t('practiceArea')}
          value={contract.practiceArea?.name}
        />
        <InfoField
          icon={Receipt}
          label={t('fullyBilled')}
          value={contract.fullyBilled ? t('yes') : t('no')}
        />
      </CardContent>
    </Card>
  );
}

export function ContractFinancialCard({ contract }: ContractCardProps) {
  const { t } = useTranslation();
  const effectivePrice = getContractEffectivePrice(contract);
  const billedPrice = Number(contract.billedPrice ?? 0);
  const balance = Number(contract.balance ?? 0);
  const formattedPrice = formatContractCurrency(effectivePrice, contract.currency);
  const formattedBilledPrice = formatContractCurrency(billedPrice, contract.currency);
  const formattedBalance = formatContractCurrency(balance, contract.currency);

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-slate-400" />
          {t('financialDetails')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <InfoField
          icon={DollarSign}
          label={t('price')}
          value={formattedPrice}
        />
        <InfoField
          icon={Percent}
          label={t('advancePayment')}
          value={
            contract.advancePayment
              ? contract.advancePaymentUnit === 'percent'
                ? `${contract.advancePayment}%`
                : formatContractCurrency(contract.advancePayment, contract.currency)
              : '-'
          }
        />
        {contract.dolarRate && (
          <InfoField
            icon={DollarSign}
            label={t('dolarRate')}
            value={`${contract.dolarRate.toLocaleString()} UZS`}
          />
        )}
        <InfoField
          icon={DollarSign}
          label={t('billedPrice')}
          value={formattedBilledPrice}
        />
        <InfoField
          icon={DollarSign}
          label={t('balance')}
          value={
            <span className={balance < 0 ? 'text-red-600' : ''}>
              {formattedBalance}
            </span>
          }
        />
      </CardContent>
    </Card>
  );
}

export function ContractTimelineCard({ contract }: ContractCardProps) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Clock className="h-5 w-5 text-slate-400" />
          {t('timeline')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <InfoField label={t('contractId')} value={String(contract.id)} mono />
        <InfoField
          icon={CalendarClock}
          label={t('startDate')}
          value={contract.startsAt ? format(new Date(contract.startsAt), 'MMM d, yyyy HH:mm') : '-'}
        />
        <InfoField
          icon={Calendar}
          label={t('endDate')}
          value={contract.endsAt ? format(new Date(contract.endsAt), 'MMM d, yyyy HH:mm') : '-'}
        />
        <InfoField
          label={t('createdAt')}
          value={
            contract.createdAt
              ? format(new Date(contract.createdAt), 'MMM d, yyyy HH:mm')
              : '-'
          }
        />
        <InfoField
          label={t('updatedAt')}
          value={
            contract.updatedAt ? (
              <span>
                {format(new Date(contract.updatedAt), 'MMM d, yyyy HH:mm')}
                <span className="text-slate-400 text-xs ml-2">
                  ({formatRelativeTime(contract.updatedAt)})
                </span>
              </span>
            ) : (
              '-'
            )
          }
        />
      </CardContent>
    </Card>
  );
}

export function ContractClientCard({ contract }: ContractCardProps) {
  const { t } = useTranslation();
  if (!contract.client) return null;

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <User className="h-5 w-5 text-slate-400" />
          {t('clientInformation')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <div className="flex items-start gap-3">
          <User className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
              {t('clientName')}
            </p>
            <Link
              to={`/app/clients/${contract.client.id}`}
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              {[contract.client.lastname, contract.client.name, contract.client.middlename]
                .filter(Boolean)
                .join(' ') || contract.client.name}
            </Link>
          </div>
        </div>
        {contract.client.email && (
          <InfoField
            icon={Mail}
            label={t('email')}
            value={contract.client.email}
            href={`mailto:${contract.client.email}`}
          />
        )}
        {contract.client.phone && (
          <InfoField
            icon={Phone}
            label={t('phone')}
            value={contract.client.phone}
            href={`tel:${contract.client.phone}`}
          />
        )}
        {(contract.client.street || contract.client.city) && (
          <InfoField
            icon={MapPin}
            label={t('address')}
            value={[
              contract.client.street,
              contract.client.city,
              contract.client.country,
              contract.client.zip,
            ]
              .filter(Boolean)
              .join(', ')}
          />
        )}
        <Link to={`/app/clients/${contract.client.id}`} className="block pt-2">
          <Button variant="outline" className="w-full">
            {t('viewClient')}
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

export function ContractSuccessPackageCard({ contract }: ContractCardProps) {
  const { t } = useTranslation();
  if (!contract.successPackage) return null;

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <BadgeCheck className="h-5 w-5 text-slate-400" />
          {t('successPackageDetails')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <InfoField
          label={t('packageName')}
          value={contract.successPackage.name}
        />
        {contract.successPackage.minClaimAmount !== undefined && (
          <InfoField
            icon={DollarSign}
            label={t('claimAmountRange')}
            value={`${formatContractCurrency(contract.successPackage.minClaimAmount, contract.currency)} - ${formatContractCurrency(contract.successPackage.maxClaimAmount ?? 0, contract.currency)}`}
          />
        )}
        <InfoField
          icon={Percent}
          label={t('advancePayment')}
          value={`${contract.successPackage.advancePayment}%`}
        />
        <InfoField
          icon={Percent}
          label={t('successPayment')}
          value={`${contract.successPackage.successPayment}%`}
        />
      </CardContent>
    </Card>
  );
}

export function ContractBillingPeriodsCard({ contract }: ContractCardProps) {
  const { t } = useTranslation();
  if (!contract.billingPeriods?.length) return null;

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <CalendarRange className="h-5 w-5 text-slate-400" />
          {t('billingPeriods')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {contract.billingPeriods.map((bp, index) => (
            <div
              key={bp.id}
              className="flex flex-col gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3"
            >
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
                {t('billingPeriodRange')} {index + 1}
              </p>
              <div className="flex items-center gap-1.5 text-sm text-slate-700">
                <CalendarClock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                <span>{bp.startDate ? format(new Date(bp.startDate), 'MMM d, yyyy') : '-'}</span>
                <span className="text-slate-400">—</span>
                <span>{bp.endDate ? format(new Date(bp.endDate), 'MMM d, yyyy') : '-'}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                    bp.fullyBilled
                      ? 'bg-green-100 text-green-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {bp.fullyBilled ? t('yes') : t('no')} — {t('fullyBilled')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function ContractQuickActionsCard({ contract }: ContractCardProps) {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900">
          {t('quickActions')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {contract.client && (
            <QuickLinkButton
              icon={User}
              label={t('viewClient')}
              to={`/app/clients/${contract.client.id}`}
            />
          )}
          <QuickLinkButton
            icon={Briefcase}
            label={t('viewMatters')}
            to={`/app/matters?contractId=${contract.id}`}
          />
          {hasAccess('bill', 'get') && (
            <QuickLinkButton
              icon={Receipt}
              label={t('viewBills')}
              to={`/app/bills?contractId=${contract.id}`}
            />
          )}
          {hasAccess('contract', 'update') && (
            <QuickLinkButton
              icon={FileText}
              label={t('editContract')}
              to={`/app/contracts/action/${contract.id}`}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
